import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useConferenceStore } from 'store/conference';
import type { TimerConfig } from 'store/conference/events/attachTimerMessageReceivedEvent';
import { attachTimerMessageReceivedEvent } from 'store/conference/events/attachTimerMessageReceivedEvent';

interface IContext {
    /** Доступное время */
    availableTime: number | null;
    /** Текущая длительность конференции */
    elapsedTime: number | null;
    /** Кол-во оставшегося времени для предупреждения об окончании конференции */
    notification: number | null;
    /** Продление конференции */
    prolongConference: () => void;
    /** Установка доступного времени */
    setAvailableTime: React.Dispatch<React.SetStateAction<IContext['availableTime']>>;
    /** Установка текущей длительности конференции */
    setElapsedTime: React.Dispatch<React.SetStateAction<IContext['elapsedTime']>>;
    /** Установка кол-ва оставшегося времени для предупреждения об окончании конференции */
    setNotification: React.Dispatch<React.SetStateAction<IContext['notification']>>;
    /** Установка конфигурации таймера не стороне vox */
    setTimerConfig: React.Dispatch<React.SetStateAction<IContext['timerConfig']>>;
    /** Конфигурация таймера не стороне vox */
    timerConfig: TimerConfig | null;
}

const Context = createContext<IContext>({
    availableTime: null,
    elapsedTime: null,
    notification: null,
    prolongConference: () => undefined,
    setAvailableTime: () => undefined,
    setElapsedTime: () => undefined,
    setNotification: () => undefined,
    setTimerConfig: () => undefined,
    timerConfig: null,
});

/** Состояние таймера встречи */
export const useTimerStore = () => useContext(Context);

const TimerStore: FC<PropsWithChildren> = ({ children }) => {
    const { VOXCallInstance } = useConferenceStore();
    const [elapsedTime, setElapsedTime] = useState<IContext['elapsedTime']>(null);
    const [availableTime, setAvailableTime] = useState<IContext['availableTime']>(null);
    const [notification, setNotification] = useState<IContext['notification']>(null);
    const [timerConfig, setTimerConfig] = useState<IContext['timerConfig']>(null);

    const prolongConference = useCallback(() => {
        if (VOXCallInstance) {
            VOXCallInstance.sendMessage(JSON.stringify({ prolong: true }));
        }
    }, [VOXCallInstance]);

    const contextValue = useMemo<IContext>(() => {
        return {
            availableTime,
            elapsedTime,
            notification,
            prolongConference,
            setAvailableTime,
            setElapsedTime,
            setNotification,
            setTimerConfig,
            timerConfig,
        };
    }, [availableTime, prolongConference, notification, timerConfig, elapsedTime]);

    useEffect(() => {
        if (VOXCallInstance) {
            attachTimerMessageReceivedEvent({ setAvailableTime, setElapsedTime, setNotification, setTimerConfig, voxCall: VOXCallInstance });
        }
    }, [VOXCallInstance]);

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export default TimerStore;
